import { DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, SignalState, signalState } from '@ngrx/signals';
import { CityState } from './city.types';
import { RegionListResponse } from 'angular-api-gateway/v1/model/regionListResponse';
import { RegionService } from 'angular-api-gateway/v1/api/region.service';
import { RegionUserResponse } from 'angular-api-gateway/v1/model/regionUserResponse';
import { Region } from 'angular-api-gateway/v1/model/region';
import { defaultApiGatewayParams } from '@lentau/core/api/gateway';

const initialState: CityState = {
  cityId: null,
  citySlug: null,
  city: null,
  cities: [],
  citiesLoading: true,
  addressRegionId: null,
  domainRegionId: null,
};

@Injectable({ providedIn: 'root' })
export class CityStore {
  private destroyRef: DestroyRef = inject(DestroyRef);
  private regionService: RegionService = inject(RegionService);

  private store: SignalState<CityState> = signalState({ ...initialState });
  private regionUserInFlight: boolean = false;

  public cityId: Signal<number> = this.store.cityId;
  public citySlug: Signal<string> = this.store.citySlug;
  public city: Signal<Region> = this.store.city;
  public cities: Signal<Region[]> = this.store.cities;
  public citiesLoading: Signal<boolean> = this.store.citiesLoading;
  public addressRegionId: Signal<number> = this.store.addressRegionId;
  public domainRegionId: Signal<number> = this.store.domainRegionId;

  public getCitiesList(): void {
    patchState(this.store, { citiesLoading: true });
    this.regionService
      .regionList({ ...defaultApiGatewayParams })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: RegionListResponse) =>
          patchState(this.store, {
            cities: data.regions,
            citiesLoading: false,
          }),
        error: () => patchState(this.store, { cities: [], citiesLoading: false }),
      });
  }

  public getUserRegion(pickupStoreRegionId?: number): void {
    // One successful region/user per page session (cold-start dedupe).
    if (this.regionUserInFlight) {
      return;
    }

    this.regionUserInFlight = true;

    this.regionService
      .regionUser({ ...defaultApiGatewayParams })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: RegionUserResponse) => {
          patchState(this.store, {
            addressRegionId: data.addressRegionId,
            domainRegionId: data.domainRegionId,
          });
          // актуально для пикапа, на неавторизованном, при заходе с поддоменом отличным от мск
          if (
            data.addressRegionId !== data.domainRegionId &&
            data.addressRegionId === 1 &&
            data.domainRegionId === pickupStoreRegionId
          ) {
            this.setCityId(data.domainRegionId);
          } else {
            this.setCityId(data.addressRegionId || data.domainRegionId);
          }
        },
        error: () => {
          this.regionUserInFlight = false;
          patchState(this.store);
        },
      });
  }

  public setCitySlug(slug: string): void {
    patchState(this.store, { citySlug: slug });
  }

  public setCityId(cityId: number): void {
    patchState(this.store, { cityId });
  }

  public setCity(city: Region): void {
    patchState(this.store, { city });
    this.setCityId(city.id);
    this.setCitySlug(city.slug);
  }
}
